import * as Sentry from "@sentry/react"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import React from "react"
import { BrowserRouter, Route, Routes } from "react-router-dom"

import { AppProvider } from "./AppProvider"
import { AuthCheck } from "./components/AuthCheck"
import { AppLayout } from "./components/common/AppLayout"
import ErrorFallback from "./components/common/ErrorBoundary"
import { LabReportsTable } from "./components/dashboard/LabReportsTable"
import { Toasts } from "./components/Toasts"
import { AnonymousSharePage } from "./pages/AnonymousShare"
import { DashboardPage } from "./pages/Dashboard"
import { LabReportPage } from "./pages/LabReportDetails"
import { LandingPage } from "./pages/Landing"
import { Login } from "./pages/Login"
import { Logout } from "./pages/Logout"
import { PatientDetailsPage } from "./pages/PatientDetails"
import { SettingsPage } from "./pages/Settings"
import { SuperAdminPage } from "./pages/SuperAdmin"

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
    },
  },
})

const App: React.FC = () => {
  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback} showDialog>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <AppProvider>
            <Toasts />
            <AuthCheck />
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/logout" element={<Logout />} />
              <Route path="/home" element={<LandingPage />} />

              <Route path="/share/:token" element={<AnonymousSharePage />} />

              <Route path="/organizations/:organizationId" element={<AppLayout />}>
                <Route index element={<DashboardPage />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="lab-reports/:reportId" element={<LabReportPage />} />
                <Route path="patients/:patientId" element={<PatientDetailsPage />} />
                <Route path="settings/*" element={<SettingsPage />} />
                <Route path="lab-report/" element={<LabReportsTable />} />
              </Route>

              <Route path="/private/*" element={<AppLayout />}>
                <Route index element={<SuperAdminPage />} />
                <Route path="*" element={<SuperAdminPage />} />
              </Route>

              <Route path="*" element={<div>Not found</div>} />
            </Routes>
            {process.env.NODE_ENV === "development" ? (
              <ReactQueryDevtools initialIsOpen={false} position="bottom" />
            ) : null}
          </AppProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  )
}

export default App
